/**
 * Everything the `<Image>` component needs to render a responsive `<picture>` for an img-opt
 * URL *without measuring the container at runtime*. The browser picks a `<source>` from the
 * media queries below, and a density-appropriate candidate from that source's `x`-descriptor
 * srcset, so `<Image>` never has to re-render on resize.
 */

/**
 * css pixel width to request for each container-size bucket, keyed by `roughSize` (how much of
 * the container the image occupies). Bigger buckets = wider viewport range.
 */
const SIZE_MAPPING = {
	xlarge: {
		s: 400,
		m: 800,
		l: 1200,
	},
	large: {
		s: 400,
		m: 600,
		l: 1000,
	},
	medium: {
		s: 400,
		m: 500,
		l: 600,
	},
	small: {
		s: 400,
		m: 400,
		l: 400,
	},
} as const;

type ContainerSize = keyof typeof SIZE_MAPPING;

/**
 * Ordered smallest viewport first. `maxWidth` is the exclusive upper bound of the viewport
 * range a bucket covers; the final entry has no `maxWidth` and is the default rendition (it
 * becomes the `<img>` fallback rather than a media-gated `<source>`).
 */
const CONTAINER_BREAKPOINTS: { maxWidth?: number; size: ContainerSize }[] = [
	{ maxWidth: 700, size: 'small' },
	{ maxWidth: 960, size: 'medium' },
	{ maxWidth: 1700, size: 'large' },
	{ size: 'xlarge' },
];

/** Pixel densities baked into every generated srcset (`1x`, `2x`, `3x`). */
const DENSITIES = [1, 2, 3];

type ImageRoughSizeOptions = 's' | 'm' | 'l';

/** URL fragment that marks a Four Seasons image-optimizer URL. */
const IMG_OPT_MARKER = '/alt/img-opt';

const isOptimizableUrl = (url: string): boolean => url.includes(IMG_OPT_MARKER);

/** The `~quality.size.crop` segment lives between two slashes; find it in a full URL. */
const findParamSegment = (url: string): string | undefined =>
	url.split('/').find(segment => segment.startsWith('~'));

/**
 * Read the intrinsic width the source asset was published at from the param segment
 * (`~65.1701.0,0000-...` -> `1701`). Falls back to a large sentinel when the size slot is
 * empty so callers never upscale past an unknown original.
 */
const readOriginalSize = (url: string): number => {
	const sizeString = findParamSegment(url)?.split('.')[1];
	return sizeString ? parseInt(sizeString, 10) : 3000;
};

/** Swap the size slot of the `~quality.size.crop` segment inside a full img-opt URL. */
const withImgOptSize = (url: string, size: number): string =>
	url
		.split('/')
		.map(segment => {
			if (!segment.startsWith('~')) {
				return segment;
			}
			const parts = segment.split('.');
			parts[1] = String(size);
			return parts.join('.');
		})
		.join('/');

type PictureSource = { media?: string; srcSet: string };

type PictureSources = {
	/** media-gated `<source>` elements, smallest viewport first */
	sources: PictureSource[];
	/** `src` for the `<img>` fallback (the default, un-gated rendition at 1x) */
	fallbackSrc: string;
	/** density srcset for the `<img>` fallback */
	fallbackSrcSet: string;
};

/**
 * Build the `<source>` list plus `<img>` fallback for a responsive `<picture>`.
 *
 * - With `exactImageWidth` the rendered width is already known, so there are no media-gated
 *   sources — just one density srcset on the `<img>`.
 * - Otherwise every entry in {@link CONTAINER_BREAKPOINTS} becomes a media-gated `<source>`
 *   whose srcset offers the `roughSize`-appropriate width at each pixel density, and the
 *   no-`maxWidth` entry becomes the `<img>` fallback.
 *
 * Every candidate width is clamped to the original asset width, and densities that clamp to a
 * width already emitted are dropped, so the CDN is never asked to upscale or serve a duplicate.
 */
const buildPictureSources = ({
	url,
	roughSize,
	exactImageWidth,
}: {
	url: string;
	roughSize: ImageRoughSizeOptions;
	exactImageWidth?: number;
}): PictureSources => {
	const originalSize = readOriginalSize(url);
	const clamp = (width: number) => Math.min(Math.ceil(width), originalSize);

	const srcSetFor = (cssWidth: number): string => {
		const emitted = new Set<number>();
		const entries: string[] = [];
		for (const density of DENSITIES) {
			const width = clamp(cssWidth * density);
			if (emitted.has(width)) {
				continue;
			}
			emitted.add(width);
			entries.push(`${withImgOptSize(url, width)} ${density}x`);
		}
		return entries.join(', ');
	};

	if (exactImageWidth) {
		return {
			sources: [],
			fallbackSrc: withImgOptSize(url, clamp(exactImageWidth)),
			fallbackSrcSet: srcSetFor(exactImageWidth),
		};
	}

	const sources: PictureSource[] = [];
	let fallbackSrc = url;
	let fallbackSrcSet = '';

	for (const { maxWidth, size } of CONTAINER_BREAKPOINTS) {
		const cssWidth = SIZE_MAPPING[size][roughSize];
		if (maxWidth === undefined) {
			fallbackSrc = withImgOptSize(url, clamp(cssWidth));
			fallbackSrcSet = srcSetFor(cssWidth);
		} else {
			// `- 0.02` mirrors the old strict `maxWidth > containerWidth`: the bucket covers
			// viewports *below* maxWidth, not including it (a 960px viewport gets `large`).
			sources.push({ media: `(max-width: ${maxWidth - 0.02}px)`, srcSet: srcSetFor(cssWidth) });
		}
	}

	return { sources, fallbackSrc, fallbackSrcSet };
};

export {
	SIZE_MAPPING,
	CONTAINER_BREAKPOINTS,
	DENSITIES,
	isOptimizableUrl,
	readOriginalSize,
	withImgOptSize,
	buildPictureSources,
};
export type { ImageRoughSizeOptions, ContainerSize, PictureSource, PictureSources };
